'use client'

import { useState, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Send,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'
import { trackContactFormSubmit, trackContactPageView, trackContactFormStart } from '@/lib/analytics'

import { FadeUp } from '@/components/ui/fade-up'
const contactSchema = z.object({
  companyName: z.string().min(1, '会社名・団体名を入力してください'),
  department: z.string().optional(),
  name: z.string().min(1, 'お名前を入力してください'),
  email: z.string().email('正しいメールアドレスを入力してください'),
  phone: z.string().optional(),
  inquiryType: z.string().min(1, 'お問い合わせ種別を選択してください'),
  message: z.string().min(10, 'お問い合わせ内容を10文字以上で入力してください'),
  privacyPolicy: z.boolean().refine((val) => val === true, {
    message: 'プライバシーポリシーに同意してください'
  })
})

type ContactFormData = z.infer<typeof contactSchema>

export function ContactContent() {
  const searchParams = useSearchParams()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [hasStartedForm, setHasStartedForm] = useState(false)
  const [selectedInquiryType, setSelectedInquiryType] = useState('')

  const inquiryType = searchParams.get('type') || 'general'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      inquiryType: inquiryType
    }
  })

  useEffect(() => {
    if (inquiryType === 'general') return

    setSelectedInquiryType(inquiryType)
    setValue('inquiryType', inquiryType)
  }, [inquiryType, setValue])

  // ページ到達時にトラッキング
  useEffect(() => {
    trackContactPageView(inquiryType)
  }, [inquiryType])

  // フォーム入力開始時にトラッキング（1回のみ）
  const handleFormStart = () => {
    if (!hasStartedForm) {
      trackContactFormStart(inquiryType)
      setHasStartedForm(true)
    }
  }

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)

    // フォーム送信イベントをトラッキング
    trackContactFormSubmit(data.inquiryType)

    try {
      const res = await fetch('/api/inquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          companyName: data.companyName,
          department: data.department,
          name: data.name,
          email: data.email,
          phone: data.phone,
          inquiryType: data.inquiryType,
          message: data.message,
          privacyPolicy: data.privacyPolicy,
        })
      })

      if (!res.ok) {
        throw new Error('Failed to submit')
      }

      setSubmitStatus('success')
      reset()
    } catch (error) {
      console.error('Failed to submit contact:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-background py-16">
        <div
          className="animate-fade-up container"
        >
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4">地域DXのご相談はお気軽に</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              お問い合わせ
            </h1>
            <p className="text-xl text-muted-foreground">
              製品・サービスに関するご質問、導入のご相談など<br />
              お気軽にお問い合わせください
            </p>
          </div>
        </div>
      </section>

      {/* 連絡先情報 */}
      <section className="py-16">
        <div className="container">
          {/* お問い合わせフォーム */}
          <FadeUp
          >
            <Card className="max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="text-2xl">お問い合わせフォーム</CardTitle>
                <CardDescription>
                  下記フォームに必要事項をご記入の上、送信してください。
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-800">送信完了</p>
                      <p className="text-sm text-green-700">
                        お問い合わせありがとうございます。2営業日以内にご連絡いたします。
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">送信エラー</p>
                      <p className="text-sm text-red-700">
                        送信中にエラーが発生しました。お手数ですが再度お試しください。
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="companyName">
                        会社名・団体名 <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="companyName"
                        {...register('companyName')}
                        placeholder="株式会社○○"
                        onFocus={handleFormStart}
                      />
                      {errors.companyName && (
                        <p className="text-sm text-red-500">{errors.companyName.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="department">部署名</Label>
                      <Input
                        id="department"
                        {...register('department')}
                        placeholder="経営企画部"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        お名前 <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        {...register('name')}
                        placeholder="山田 太郎"
                      />
                      {errors.name && (
                        <p className="text-sm text-red-500">{errors.name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">
                        メールアドレス <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder="yamada@example.com"
                      />
                      {errors.email && (
                        <p className="text-sm text-red-500">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">電話番号</Label>
                      <Input
                        id="phone"
                        type="tel"
                        {...register('phone')}
                        placeholder="082-123-4567"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="inquiryType">
                        お問い合わせ種別 <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={selectedInquiryType}
                        onValueChange={(value) => {
                          setSelectedInquiryType(value)
                          setValue('inquiryType', value)
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consultation">1時間無料相談</SelectItem>
                          <SelectItem value="ai-factory">AI活用・AIコンテンツ生産工場について</SelectItem>
                          <SelectItem value="service">サービスについて</SelectItem>
                          <SelectItem value="visit">Digital Pride Hiroshima 見学</SelectItem>
                          <SelectItem value="training">出向教育について</SelectItem>
                          <SelectItem value="partnership">パートナーシップについて</SelectItem>
                          <SelectItem value="other">その他</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.inquiryType && (
                        <p className="text-sm text-red-500">{errors.inquiryType.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      {...register('message')}
                      placeholder="お問い合わせ内容をご記入ください"
                      rows={6}
                    />
                    {errors.message && (
                      <p className="text-sm text-red-500">{errors.message.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-start">
                      <Checkbox
                        id="privacyPolicy"
                        onCheckedChange={(checked) => setValue('privacyPolicy', checked as boolean)}
                      />
                      <Label htmlFor="privacyPolicy" className="ml-2 text-sm">
                        <a href="/privacy" className="underline">プライバシーポリシー</a>
                        に同意する <span className="text-red-500">*</span>
                      </Label>
                    </div>
                    {errors.privacyPolicy && (
                      <p className="text-sm text-red-500 ml-6">{errors.privacyPolicy.message}</p>
                    )}
                  </div>

                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm text-amber-800">
                      ※営業に関する問い合わせは1件あたりビール1杯分（500円）を請求いたします
                    </p>
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>送信中...</>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        送信する
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-center mb-12">よくあるご質問</h2>

            <div className="space-y-4">
              {[
                {
                  q: '1時間無料相談では何を相談できますか？',
                  a: 'DX推進の課題、技術選定、システム構築、人材育成など、デジタル化に関するあらゆるご相談を承ります。オンラインでも対面でも可能です。'
                },
                {
                  q: 'Digital Pride Hiroshimaの見学はできますか？',
                  a: 'はい、可能です。実証ラボでの開発の様子や、文化翻訳型DXの展示を直接ご体験いただけます。お問い合わせ種別から「Digital Pride Hiroshima 見学」を選択してお申し込みください。'
                },
                {
                  q: 'Digital Pride Hiroshimaを貸し切ることはできますか？',
                  a: '可能です。企業研修、イベント、ワークショップなど、様々な用途でご利用いただけます。詳細はお問い合わせください。'
                },
                {
                  q: '出向教育の期間と費用は？',
                  a: '半年〜1年間のプログラムで、企業様のニーズに合わせてカスタマイズします。費用は個別相談となりますので、お気軽にお問い合わせください。'
                },
                {
                  q: '広島以外の地域でも対応可能ですか？',
                  a: '特別の場合を除き、現時点では対応していません。'
                }
              ].map((faq, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg">{faq.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}
